import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AlertModal from '../components/modals/AlertModal';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const [availableUgp, setAvailableUgp] = useState([]);
  const [loadingAvailableUgp, setLoadingAvailableUgp] = useState(false);
  const [userGroupsPeriods, setUserGroupsPeriods] = useState([]); // groups_periods from user profile
  const [sessionExpired, setSessionExpired] = useState(false);
  const [periodExpired, setPeriodExpired] = useState(false);
  const [periodExpiredMessage, setPeriodExpiredMessage] = useState('');
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [autoSwitchedMessage, setAutoSwitchedMessage] = useState('');
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [loginDisabledMessage, setLoginDisabledMessage] = useState('');
  const [mainPlatformAccess, setMainPlatformAccess] = useState(null);
  const [loadingMainPlatformAccess, setLoadingMainPlatformAccess] = useState(false);
  const [reauthEmail, setReauthEmail] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);

  const loadMainPlatformAccess = useCallback(async (role) => {
    if (!user?.id || !token) {
      setMainPlatformAccess(null);
      setLoadingMainPlatformAccess(false);
      return;
    }

    setLoadingMainPlatformAccess(true);
    try {
      const params = new URLSearchParams();
      if (role?.groups_periods_id) params.set('groups_periods_id', role.groups_periods_id);
      if (role?.id) params.set('user_groups_periods_id', role.id);
      if (role?.period_id) params.set('periodId', role.period_id);
      if (role?.group_name) params.set('group_name', role.group_name);
      if (role?.relationship_type) params.set('relationship_type', role.relationship_type);
      if (role?.parent_group_name) params.set('parent_group_name', role.parent_group_name);
      const response = await fetch(`/api/page-configurations/platform-access?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.status === 401) {
        setSessionExpired(true);
        setMainPlatformAccess(null);
        return;
      }
      const data = await response.json();
      setMainPlatformAccess(response.ok && data?.data?.hasAccess === true);
    } catch (error) {
      console.error('Error loading AcuitySIS shell access:', error);
      setMainPlatformAccess(false);
    } finally {
      setLoadingMainPlatformAccess(false);
    }
  }, [user?.id, token]);

  // Fetch current role - memoized with useCallback
  const loadCurrentRole = useCallback(async () => {
    if (!user?.id || !token) {
      setCurrentRole(null);
      setMainPlatformAccess(null);
      return;
    }
    
    setLoadingRole(true);
    let roleForAccess = null;
    setPeriodExpired(false);
    setPeriodExpiredMessage('');
    try {
      const response = await fetch(`/api/user-groups-periods/user/${user.id}/current-membership`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      // Check for auto-switched role (period expired but found valid alternative)
      if (data.success && data.data && data.autoSwitched) {
        // Auto-switch to the valid role
        const validRole = data.data;
        roleForAccess = validRole;
        setCurrentRole(validRole);
        setPeriodExpired(false);
        setPeriodExpiredMessage('');
        setAutoSwitched(true);
        setAutoSwitchedMessage('Caution: Switching to a valid account');
        
        // Update user's current_ugp_id to the new valid role
        try {
          await fetch(`/api/users/${user.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              current_ugp_id: validRole.id
            })
          });
        } catch (error) {
          console.error('Error updating current_ugp_id:', error);
        }
        
        // Auto-close the notice after 3 seconds
        setTimeout(() => {
          setAutoSwitched(false);
          setAutoSwitchedMessage('');
        }, 3000);
        
        return;
      }
      
      // Check for period expiration errors (403) - no valid alternative found
      if (response.status === 403 && (data.code === 'ROLE_PERIOD_EXPIRED' || data.code === 'ORGANIZATION_PERIOD_EXPIRED')) {
        setPeriodExpired(true);
        setPeriodExpiredMessage(data.message || 'Access no longer valid');
        setCurrentRole(null);
        setAutoSwitched(false);
        roleForAccess = null;
        return;
      }
      
      if (data.success && data.data) {
        roleForAccess = data.data;
        setCurrentRole(data.data);
        setPeriodExpired(false);
        setPeriodExpiredMessage('');
        setAutoSwitched(false);
      } else {
        setCurrentRole(null);
      }
    } catch (error) {
      console.error('Error loading current role:', error);
      setCurrentRole(null);
    } finally {
      setLoadingRole(false);
      await loadMainPlatformAccess(roleForAccess);
    }
  }, [user?.id, token, loadMainPlatformAccess]);

  // Fetch available organization roles - memoized with useCallback
  // This is shared across ProtectedRoute and Navigation to avoid duplicate queries
  const loadAvailableUgp = useCallback(async () => {
    if (!user?.id || !token) {
      setAvailableUgp([]);
      return;
    }
    
    setLoadingAvailableUgp(true);
    try {
      const response = await fetch(`/api/user-groups-periods/user/${user.id}/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success && data.data && Array.isArray(data.data)) {
        setAvailableUgp(data.data);
      } else {
        setAvailableUgp([]);
      }
    } catch (error) {
      console.error('Error loading available organization roles:', error);
      setAvailableUgp([]);
    } finally {
      setLoadingAvailableUgp(false);
    }
  }, [user?.id, token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await fetch('/api/auth/profile', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            
            // Check for login disabled error (403)
            if (response.status === 403 && data.code === 'LOGIN_DISABLED') {
              setLoginDisabled(true);
              setLoginDisabledMessage(data.message || 'Access denied. Your account login has been disabled.');
              setUser(null);
              setToken(null);
              setCurrentRole(null);
              localStorage.removeItem('token');
              return;
            }
            
            if (data.success && data.data && data.data.user) {
              // Check canLogin field
              if (data.data.user.canLogin === false) {
                setLoginDisabled(true);
                setLoginDisabledMessage('Access denied. Your account login has been disabled.');
                setUser(null);
                setToken(null);
                setCurrentRole(null);
                localStorage.removeItem('token');
                return;
              }
              
              setUser(data.data.user);
              // Extract and store groups_periods from user profile (shared across components)
              if (data.data.user.groupsPeriods && Array.isArray(data.data.user.groupsPeriods)) {
                setUserGroupsPeriods(data.data.user.groupsPeriods);
              } else {
                setUserGroupsPeriods([]);
              }
              setLoginDisabled(false);
              setLoginDisabledMessage('');
            } else {
              // Token is invalid, clear it
              localStorage.removeItem('token');
              setToken(null);
              setCurrentRole(null);
            }
          } else {
            // Check for login disabled error (403)
            if (response.status === 403) {
              const data = await response.json();
              if (data.code === 'LOGIN_DISABLED') {
                setLoginDisabled(true);
                setLoginDisabledMessage(data.message || 'Access denied. Your account login has been disabled.');
                setUser(null);
                setToken(null);
                setCurrentRole(null);
                localStorage.removeItem('token');
                return;
              }
            }
            
            // Token is invalid, clear it
            localStorage.removeItem('token');
            setToken(null);
            setCurrentRole(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setCurrentRole(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Load role and organization roles when user is set
  useEffect(() => {
    if (user?.id && token && !loading) {
      loadCurrentRole();
      loadAvailableUgp(); // Load organization roles once, shared across components
    } else if (!user) {
      setCurrentRole(null);
      setAvailableUgp([]);
    }
  }, [user?.id, token, loading, loadCurrentRole, loadAvailableUgp]);

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        const { user: userData, token: newToken } = data.data;
        setUser(userData);
        setToken(newToken);
        localStorage.setItem('token', newToken);
        // Role will be loaded by useEffect when user is set
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, firstName, lastName })
      });

      const data = await response.json();

      if (data.success) {
        const { user: userData, token: newToken } = data.data;
        setUser(userData);
        setToken(newToken);
        localStorage.setItem('token', newToken);
        // Role will be loaded by useEffect when user is set
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setCurrentRole(null);
    setAvailableUgp([]);
    setUserGroupsPeriods([]);
    setPeriodExpired(false);
    setPeriodExpiredMessage('');
    setAutoSwitched(false);
    setAutoSwitchedMessage('');
    setLoginDisabled(false);
    setLoginDisabledMessage('');
    setMainPlatformAccess(null);
    setLoadingMainPlatformAccess(false);
    localStorage.removeItem('token');
  };

  // Centralized authenticated fetch with 401 handling
  const fetchWithAuth = async (input, init = {}) => {
    const headers = {
      ...(init.headers || {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
    const requestInit = { ...init, headers };
    const response = await fetch(input, requestInit);
    if (response.status === 401) {
      // Attempt to parse a code for better UX (TOKEN_EXPIRED vs others)
      try {
        const clone = response.clone();
        const data = await clone.json();
        if (data && (data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN')) {
          setSessionExpired(true);
        } else {
          setSessionExpired(true);
        }
      } catch (_) {
        setSessionExpired(true);
      }
    }
    return response;
  };

  const updateProfile = async (firstName, lastName) => {
    try {
      const requestBody = { firstName, lastName };
      
      // Include version for conflict detection if available
      if (user?.updatedAt) {
        requestBody.version = user.updatedAt;
        console.log('Profile update - sending version:', user.updatedAt);
      }

      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        return { success: true, message: data.message };
      } else {
        // Handle version conflict specifically
        if (data.code === 'VERSION_CONFLICT') {
          return { 
            success: false, 
            message: data.message,
            code: 'VERSION_CONFLICT',
            currentVersion: data.currentVersion
          };
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const body = { currentPassword, newPassword };
      if (user?.updatedAt) {
        body.version = user.updatedAt;
      }
      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        return { success: true, message: data.message };
      } else if (data.code === 'VERSION_CONFLICT') {
        return { success: false, code: 'VERSION_CONFLICT', message: data.message, currentVersion: data.currentVersion };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.error('Password change error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const value = {
    user,
    token,
    loading,
    currentRole,
    loadingRole,
    loadCurrentRole,
    availableUgp,
    loadingAvailableUgp,
    loadAvailableUgp,
    userGroupsPeriods, // groups_periods from user profile (shared, avoids duplicate queries)
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    fetchWithAuth,
    sessionExpired,
    periodExpired,
    periodExpiredMessage,
    autoSwitched,
    autoSwitchedMessage,
    setAutoSwitched,
    loginDisabled,
    loginDisabledMessage,
    mainPlatformAccess,
    loadingMainPlatformAccess,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Login Disabled Modal */}
      {loginDisabled && (
        <AlertModal
          isOpen={loginDisabled}
          onClose={() => {
            // Don't allow closing - user must logout
          }}
          variant="error"
          subject="Access Denied"
          body={loginDisabledMessage || 'Your account login has been disabled. Please contact support or logout.'}
          icon="🚫"
          showCloseButton={false}
          allowOverlayClose={false}
          autoClose={false}
          actions={[
            {
              label: 'Logout',
              onClick: () => logout(),
              variant: 'primary',
              style: { background: '#dc3545' }
            }
          ]}
          zIndex={9999}
        />
      )}
      {/* Auto-Switched Notice Modal */}
      {autoSwitched && (
        <AlertModal
          isOpen={autoSwitched}
          onClose={() => setAutoSwitched(false)}
          variant="warning"
          subject="Caution: Switching to a valid account"
          body={autoSwitchedMessage || 'Your previous role period has expired. We\'ve automatically switched you to a valid account.'}
          icon="⚠️"
          showCloseButton={true}
          allowOverlayClose={true}
          autoClose={true}
          autoCloseDelay={3000}
          actions={[]}
          zIndex={10000}
        />
      )}
      {/* Period Expired Modal */}
      {periodExpired && (
        <AlertModal
          isOpen={periodExpired}
          onClose={() => {
            // Don't allow closing - user must logout
          }}
          variant="error"
          subject="Access No Longer Valid"
          body={periodExpiredMessage || 'Your access period has expired. Please contact support or logout.'}
          icon="⚠️"
          showCloseButton={false}
          allowOverlayClose={false}
          autoClose={false}
          actions={[
            {
              label: 'Logout',
              onClick: () => logout(),
              variant: 'primary',
              style: { background: '#dc3545' }
            }
          ]}
          zIndex={9999}
        />
      )}
      {sessionExpired && (
        <div style={modalStyles.backdrop}>
          <div style={modalStyles.modal}>
            <h3 style={modalStyles.title}>Session expired</h3>
            <p style={modalStyles.text}>Please log in again to continue. Your current page and data are preserved.</p>
            {reauthError && <div style={modalStyles.error}>{reauthError}</div>}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setReauthError('');
                setReauthLoading(true);
                const result = await login(reauthEmail, reauthPassword);
                setReauthLoading(false);
                if (result.success) {
                  setSessionExpired(false);
                  setReauthEmail('');
                  setReauthPassword('');
                } else {
                  setReauthError(result.message || 'Login failed. Please try again.');
                }
              }}
              style={modalStyles.form}
            >
              <label style={modalStyles.label}>Email</label>
              <input
                type="email"
                value={reauthEmail}
                onChange={(e) => setReauthEmail(e.target.value)}
                required
                style={modalStyles.input}
                autoFocus
              />
              <label style={modalStyles.label}>Password</label>
              <input
                type="password"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                required
                style={modalStyles.input}
              />
              <div style={modalStyles.actions}>
                <button type="submit" disabled={reauthLoading} style={modalStyles.primaryBtn}>
                  {reauthLoading ? 'Logging in…' : 'Log In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

const modalStyles = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
  },
  modal: {
    background: 'white', borderRadius: '12px', padding: '20px', maxWidth: '420px', width: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
  },
  title: { margin: '0 0 10px 0', color: '#111827' },
  text: { margin: '0 0 18px 0', color: '#4b5563' },
  actions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  primaryBtn: { background: '#4f46e5', color: 'white', padding: '10px 14px', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  form: { display: 'grid', gap: '10px' },
  label: { fontSize: '0.85rem', color: '#475569' },
  input: { padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', outline: 'none' },
  error: { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '8px 10px', borderRadius: '8px', marginBottom: '8px' }
};
